// @flow
import React from 'react';
import {Text, Caption} from 'sail/Text';
import {ButtonLink} from 'sail/Button';
import Box from 'sail/Box';
import Menu, {MenuItem} from 'sail/Menu';
import Icon from 'sail/Icon';
import {chevronDown} from 'sail/Icon/Assets';
import Group from 'sail/Group';
import FlagIcon, {FLAG_COUNTRY_CODES} from 'sail/FlagIcon';
import DeviceIcon from 'sail/DeviceIcon';
import {defineMessages, useIntl} from 'react-intl';

// Apparently enums are experimental, this is pretty close
type Labels = 'customerLocation' | 'theme' | 'size';

type DropdownProps = {|
  labelEnum: Labels,
  value: string,
  onChange: Function,
  optionMap: {},
|};

type DropdownInfoBoxProps = {|
  title: string,
  body: string,
  width: string,
|};

const labelMessages = defineMessages({
  customerLocation: {
    id: 'docsApp.demo.paymentElement.label.customerLocation',
    description: 'Location of the customer (label for a country dropdown)',
    defaultMessage: 'Customer location',
  },
  theme: {
    id: 'docsApp.demo.paymentElement.label.theme',
    description: 'Label for a theme dropdown (e.g. dark blue, night, default)',
    defaultMessage: 'Theme',
  },
  size: {
    id: 'docsApp.demo.paymentElement.label.size',
    description: 'Label for a size dropdown (e.g. desktop, mobile)',
    defaultMessage: 'Size',
  },
});

const titleMessages = defineMessages({
  customerLocation: {
    id: 'docsApp.demo.paymentElement.title.customerLocation',
    description: 'Title of section',
    defaultMessage: 'Every country pays differently',
  },
  theme: {
    id: 'docsApp.demo.paymentElement.title.theme',
    description: 'Title of section',
    defaultMessage: 'Customize it',
  },
});

const bodyMessages = defineMessages({
  customerLocation: {
    id: 'docsApp.demo.paymentElement.body.customerLocation',
    description: 'Body of section',
    defaultMessage:
      'The Payment Element supports 135+ currencies. Only a sample is shown here. Stripe automatically reorders payment methods to increase potential conversion.',
  },
  theme: {
    id: 'docsApp.demo.paymentElement.body.theme',
    description: 'Body of section',
    defaultMessage:
      'Create a theme to match your brand with the Appearance API.',
  },
});

function DropdownInfoBox({title, body, width}: DropdownInfoBoxProps) {
  return (
    <Box
      divider={{side: 'top', color: 'light'}}
      padding={{top: 12, bottom: 12, left: 12, right: 12}}
      style={{width}}
      margin={{top: 8}}
    >
      <Group spacing={8} direction="horizontal" alignItems="baseline">
        <Icon icon="info" color="gray" size={12} />
        <Text color="gray500" lineHeight={20} weight="medium">
          {title}
        </Text>
      </Group>
      <Box padding={{top: 8, left: 20}}>
        <Caption>{body}</Caption>
      </Box>
    </Box>
  );
}

export default function Dropdown({
  labelEnum,
  value,
  onChange,
  optionMap,
}: DropdownProps) {
  const intl = useIntl();
  const itemData: Array<any> = Object.values(optionMap);
  if (itemData[0] && itemData[0].sortingWeight) {
    itemData.sort((a, b) => a.sortingWeight - b.sortingWeight);
  }

  const items = itemData.map((d) => (
    <MenuItem key={d.key} onClick={(e) => onChange(d.key)}>
      <Group direction="horizontal" spacing={8} alignItems="center">
        <Icon
          icon="check"
          size={12}
          color={value === d.key ? 'selected' : 'white'}
        />
        {labelEnum === 'customerLocation' && (
          <FlagIcon countryCode={FLAG_COUNTRY_CODES[d.key.toUpperCase()]} />
        )}
        {d.label}
      </Group>
    </MenuItem>
  ));

  if (labelEnum === 'customerLocation') {
    items.push(
      <DropdownInfoBox
        title={intl.formatMessage(titleMessages.customerLocation)}
        body={intl.formatMessage(bodyMessages.customerLocation)}
        width="250px"
      />,
    );
  }

  if (labelEnum === 'theme') {
    items.push(
      <DropdownInfoBox
        title={intl.formatMessage(titleMessages.theme)}
        body={intl.formatMessage(bodyMessages.theme)}
        width="185px"
      />,
    );
  }

  return (
    <Group direction="horizontal" alignItems="center" spacing={8}>
      <Text weight="medium" color="gray" lineHeight={20} size={14}>
        {intl.formatMessage(labelMessages[labelEnum])}
      </Text>
      <Group direction="horizontal" alignItems="center" spacing={4}>
        {labelEnum === 'customerLocation' && (
          <FlagIcon countryCode={FLAG_COUNTRY_CODES[value.toUpperCase()]} />
        )}
        {labelEnum === 'size' && (
          <DeviceIcon deviceType={optionMap[value].key} />
        )}
        <Menu items={items}>
          <ButtonLink
            label={optionMap[value].label}
            icon={chevronDown}
            iconPosition="right"
          />
        </Menu>
      </Group>
    </Group>
  );
}
